import React, { useEffect, useState } from 'react';
import { downloadAuthenticatedFile } from '../../utils/downloadHelper';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Collapse,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    AttachFile,
    Delete,
    ExpandLess,
    ExpandMore,
    GetApp,
    Description,
} from '@material-ui/icons';
import Swal from 'sweetalert2';
import { Modal, PrimaryButton, SecondaryButton } from '../../ui';
import { ClientQuoteDetailsApi } from '../../../client/clientQuoteDetails/service/clientQuoteDetailsApi';

const useStyles = makeStyles((theme) => ({
    sectionCard: {
        border: `1px solid #E2E8F0`,
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    sectionHeader: {
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sectionHeaderExpanded: {
        marginBottom: theme.spacing(1.75),
    },
    sectionTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 700,
        color: '#0E4A64',
        lineHeight: 1.3,
    },
    sectionBody: {
        padding: 0,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.2, 1.5),
        marginBottom: theme.spacing(1),
        backgroundColor: '#F8FAFC',
        borderRadius: 10,
        border: '1px solid #E2E8F0',
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    fileIcon: {
        color: '#64748B',
    },
    fileActions: {
        display: 'flex',
        gap: theme.spacing(0.5),
    },
    uploadButton: {
        backgroundColor: '#00C2E0',
        color: '#FFFFFF',
        borderRadius: 999,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#00acc1',
        },
    },
    emptyMessage: {
        color: '#64748B',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    inputFile: {
        display: 'none',
    },
    fileLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
        border: '2px dashed #CBD5E1',
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'center',
        '&:hover': {
            borderColor: '#00C2E0',
            backgroundColor: '#F0FAFF',
        },
    },
    modalActions: {
        display: 'flex',
        gap: 12,
        width: '100%',
        justifyContent: 'flex-end',
    },
    loadingButtonContent: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
    },
}));

const QuoteBudgetFileSection = ({ quoteId, role, currentLanguage, serviceFiles, status, onFileChange }) => {
    const classes = useStyles();

    const shared = currentLanguage?.bepiService?.shared || {};
    const t = (key, fallback) => {
        const value = shared[key];
        return typeof value === 'string' && value.length > 0 ? value : fallback;
    };

    const isAdmin = role === 'admin';
    const isClient = role === 'client';
    const isPendente = status === 'pendente de aprovação';

    // Para orcamento vamos filtrar apenas arquivos do tipo 'orcamento' se tivessem tipagem (igual serviceFilesSection.js)
    // Aqui assumimos que o mock inicial ou a filtragem pode ser feito com o serviceFiles
    const orcamentoFilesFromApi = Array.isArray(serviceFiles) 
        ? serviceFiles.filter(f => f.type === 'orcamento')
        : [];

    const [expanded, setExpanded] = useState(true);
    const [budgetFiles, setBudgetFiles] = useState(orcamentoFilesFromApi);
    const [modalOpen, setModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [processingAction, setProcessingAction] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileNameInput, setFileNameInput] = useState('');
    const [errorModal, setErrorModal] = useState({
        isOpen: false,
        title: t('shared_details_error_label', 'Erro'),
        message: '',
    });

    useEffect(() => {
        if (Array.isArray(serviceFiles)) {
            const filtered = serviceFiles.filter(f => f.type === 'orcamento');
            setBudgetFiles(filtered);
        }
    }, [serviceFiles]);

    useEffect(() => {
        // Atualiza conforme mock ou se tiver algo da prop 
        // (A logica de InvoiceSection tbm manda evento pra cima)
        if (typeof onFileChange === 'function') {
            onFileChange(budgetFiles);
        }
    }, [budgetFiles, onFileChange]);

    const toggleSection = () => {
        setExpanded((prev) => !prev);
    };

    const openErrorModal = (message) => {
        setErrorModal({
            isOpen: true,
            title: t('shared_details_error_label', 'Erro'),
            message,
        });
    };

    const closeErrorModal = () => {
        setErrorModal((prev) => ({ ...prev, isOpen: false }));
    };

    const handleOpenModal = () => {
        setSelectedFile(null);
        setFileNameInput('');
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (uploading) return;
        setModalOpen(false);
        setSelectedFile(null);
        setFileNameInput('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            if (!fileNameInput) {
                setFileNameInput(file.name.replace(/\.[^/.]+$/, ''));
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            openErrorModal(t('shared_details_select_file_warning', 'Selecione um arquivo para continuar.'));
            return;
        }

        if (!fileNameInput.trim()) {
            openErrorModal(t('shared_details_enter_name_warning', 'Informe um nome para o arquivo.'));
            return;
        }

        setUploading(true);

        try {
            await ClientQuoteDetailsApi.uploadBudgetFile(quoteId, selectedFile, fileNameInput);

            handleCloseModal();

            Swal.fire(
                'Sucesso', 
                `Arquivo '${fileNameInput.trim()}' anexado com sucesso.`, 
                'success'
            ).then(() => {
                window.location.reload();
            });

        } catch (error) {
            console.error('Erro ao enviar o orçamento', error);
            openErrorModal('Não foi possível anexar o arquivo. Tente novamente.');
        } finally {
            setUploading(false);
        }
    };

    const getBudgetDisplayName = (file) => {
        if (!file || typeof file !== 'object') return '';

        return file.fileName
            || file.file_name
            || file.budget_file_name
            || file.budgetFileName
            || file.orcamento_file_name
            || file.orcamentoFileName
            || file.nome
            || '';
    };

    const handleAcceptBudget = async () => {
        const confirm = await Swal.fire({
            title: 'Aceitar Orçamento',
            text: 'Deseja aceitar este orçamento e prosseguir para manutenção?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Aceitar',
            cancelButtonText: 'Cancelar',
        });

        if (!confirm.isConfirmed) return;

        setProcessingAction(true);
        try {
            await ClientQuoteDetailsApi.acceptBudgetByQuoteId(quoteId);
            Swal.fire('Sucesso', 'Orçamento aceito com sucesso.', 'success').then(() => {
                window.location.reload();
            });
        } catch (error) {
            console.error('Erro ao aceitar orçamento', error);
            openErrorModal('Não foi possível aceitar o orçamento.');
        } finally {
            setProcessingAction(false);
        }
    };

    const handleRefuseBudget = async () => {
        const { value: motivo } = await Swal.fire({
            title: 'Recusar Orçamento',
            input: 'textarea',
            inputLabel: 'Motivo da recusa',
            inputPlaceholder: 'Informe o motivo...',
            inputAttributes: {
                'aria-label': 'Informe o motivo da recusa'
            },
            showCancelButton: true,
            confirmButtonText: 'Recusar',
            cancelButtonText: 'Cancelar',
        });

        if (motivo === undefined) return; // Cancelou

        if (!motivo.trim()) {
            openErrorModal('O motivo da recusa é obrigatório.');
            return;
        }

        setProcessingAction(true);
        try {
            await ClientQuoteDetailsApi.refuseBudgetByQuoteId(quoteId, motivo);
            Swal.fire('Sucesso', 'Orçamento recusado com sucesso.', 'success').then(() => {
                window.location.reload();
            });
        } catch (error) {
            console.error('Erro ao recusar orçamento', error);
            openErrorModal('Não foi possível recusar o orçamento.');
        } finally {
            setProcessingAction(false);
        }
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Remover Arquivo',
            text: 'Deseja remover este arquivo de orçamento?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('shared_details_remove_invoice_confirm_btn', 'Remover'),
            cancelButtonText: t('shared_details_cancel_btn', 'Cancelar'),
        });

        if (!confirm.isConfirmed) return;
        setBudgetFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const handleDownload = async (file) => {
        if (file.downloadUrl && !file.isMock) {
            try {
                await downloadAuthenticatedFile(file.downloadUrl, getBudgetDisplayName(file));
            } catch (error) {
                console.error('Falha ao baixar arquivo:', error);
            }
        } else {
            // Mock do Swal caso seja local e nao real (seguindo logica igual invoiceSection)
            const messageTemplate = t('shared_details_download_info_msg', 'O arquivo #vr0# será baixado.');
            Swal.fire('Info', messageTemplate.replace('#vr0#', getBudgetDisplayName(file)), 'info');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const showSection = status?.toLowerCase() === 'aguardando orçamento' || (budgetFiles && budgetFiles.length > 0);
    
    if (!showSection) return null;

    return (
        <>
            <Card className={classes.sectionCard}>
                <Box
                    className={`${classes.sectionHeader} ${expanded ? classes.sectionHeaderExpanded : ''}`}
                    onClick={toggleSection}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleSection();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <Typography className={classes.sectionTitle}>
                        ARQUIVO DE ORÇAMENTO
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleSection();
                        }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>
                <Collapse in={expanded}>
                    <Box className={classes.sectionBody}>
                        {isAdmin && (
                            <Button
                                variant="contained"
                                className={classes.uploadButton}
                                startIcon={<AttachFile />}
                                onClick={handleOpenModal}
                                style={{ marginBottom: 16 }}
                            >
                                Anexar Arquivo de Orçamento
                            </Button>
                        )}

                        {budgetFiles.length === 0 ? (
                            <Typography className={classes.emptyMessage}>
                                Nenhum arquivo de orçamento anexado.
                            </Typography>
                        ) : (
                            budgetFiles.map((file) => (
                                <Box key={file.id} className={classes.fileItem}>
                                    <Box className={classes.fileInfo}>
                                        <Description className={classes.fileIcon} />
                                        <Box>
                                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                                                {getBudgetDisplayName(file)}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Enviado em: {formatDate(file.createdAt || file.data_upload)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={classes.fileActions}>
                                        <IconButton
                                            size="small"
                                            title={t('shared_details_download_tooltip', 'Baixar')}
                                            onClick={() => handleDownload(file)}
                                            style={{ color: '#00C2E0' }}
                                        >
                                            <GetApp />
                                        </IconButton>
                                        {isAdmin && file.isMock && (
                                            <IconButton
                                                size="small"
                                                title={t('shared_details_delete_tooltip', 'Excluir')}
                                                onClick={() => handleDelete(file.id)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            ))
                        )}

                        {isClient && isPendente && budgetFiles.length > 0 && (
                            <Box display="flex" gap={2} mt={2}>
                                <PrimaryButton 
                                    onClick={handleAcceptBudget}
                                    disabled={processingAction}
                                >
                                    {processingAction ? <CircularProgress size={20} color="inherit" /> : 'Aceitar Orçamento'}
                                </PrimaryButton>
                                <SecondaryButton 
                                    onClick={handleRefuseBudget}
                                    disabled={processingAction}
                                >
                                    {processingAction ? <CircularProgress size={20} color="inherit" /> : 'Recusar Orçamento'}
                                </SecondaryButton>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Card>

            <Modal
                isOpen={modalOpen}
                onCancel={handleCloseModal}
                showIcon={false}
                align="left"
                maxWidth="560px"
                title="Adicionar Arquivo de Orçamento (PDF)"
                footer={(
                    <div className={classes.modalActions}>
                        <SecondaryButton
                            type="button"
                            onClick={handleCloseModal}
                            disabled={uploading}
                        >
                            {t('shared_details_cancel_btn', 'Cancelar')}
                        </SecondaryButton>
                        <PrimaryButton
                            type="button"
                            onClick={handleUpload}
                            disabled={uploading || !selectedFile}
                        >
                            {uploading
                                ? (
                                    <span className={classes.loadingButtonContent}>
                                        <CircularProgress size={18} color="inherit" />
                                        {t('shared_details_uploading_btn', 'Enviando...')}
                                    </span>
                                )
                                : t('shared_details_save_btn', 'Salvar')}
                        </PrimaryButton>
                    </div>
                )}
            >
                <TextField
                    fullWidth
                    label="Nome do Arquivo"
                    value={fileNameInput}
                    onChange={(e) => setFileNameInput(e.target.value)}
                    variant="outlined"
                    size="small"
                    style={{ marginBottom: 16, marginTop: 8 }}
                    placeholder="Informe o nome do arquivo de orçamento"
                />

                <input
                    type="file"
                    id="budget-file-input"
                    accept=".pdf"
                    className={classes.inputFile}
                    onChange={handleFileSelect}
                />
                <label htmlFor="budget-file-input" className={classes.fileLabel}>
                    <AttachFile />
                    <Typography variant="body2">
                        {selectedFile ? selectedFile.name : t('shared_details_select_file_msg', 'Selecionar arquivo')}
                    </Typography>
                </label>

                {selectedFile && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
                        {t('shared_details_file_size_label', 'Tamanho do Arquivo:')} {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                )}
            </Modal>

            <Modal
                isOpen={errorModal.isOpen}
                type="error"
                title={errorModal.title}
                message={errorModal.message}
                confirmText="OK"
                onConfirm={closeErrorModal}
                onCancel={null}
            />
        </>
    );
};

QuoteBudgetFileSection.propTypes = {
    quoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
    currentLanguage: PropTypes.object,
    serviceFiles: PropTypes.array,
    status: PropTypes.string,
    onFileChange: PropTypes.func,
};

QuoteBudgetFileSection.defaultProps = {
    quoteId: null,
    role: 'client',
    currentLanguage: {},
    serviceFiles: [],
    status: '',
    onFileChange: null,
};

export default QuoteBudgetFileSection;
